import type { AssetTemplates, ShopifyAsset } from '~/modules/shopify';
import type { IAssignedLoading } from '~/stores/assigned';
import type { IQueryThemePages } from '~/modules/apps';

import {
  useFeatureFlagChecker,
  useQueryThemePages,
  useRefetchThemePage,
  useShopInfo,
} from '~/modules/apps';
import { createDraftAssetTemplate } from '../modules/modal-pick-template/utils';
import { useUnpublishedThemePages } from '~/modules/campaign-template';
import { SUPPORT_FETCH_ASSET_TEMPLATE_ASSIGNED } from '../constants';
import { useFetchTemplateAssigned } from '~/presentation/common';
import { buildAssetsWithAssigned } from '../helpers/asset';
import { useCallback, useEffect, useMemo } from 'react';
import { useAssignedStore } from '~/stores/assigned';
import { useShopifyAsset } from '~/modules/shopify';
import { TemplateType } from '~/types/template';

export interface IAssetTemplateListingResult {
  defaultThemePages: IQueryThemePages['defaultThemePages'];
  isLoadingAsset: boolean;
  assignedLoading: IAssignedLoading;
  assetsWithAssigned: ShopifyAsset[];
  draftAssetTemplates: AssetTemplates;
  isFetchingUnpublishedThemePages: boolean;
  handleRefetchAsset: () => Promise<void>;
}
export const useInitializeAssetTemplateListing = (): IAssetTemplateListingResult => {
  const shopInfo = useShopInfo();
  const refetchThemePage = useRefetchThemePage();
  const { unpublishedThemePages, isFetchingUnpublishedThemePages } = useUnpublishedThemePages();

  const assetsWithAssigned = useAssignedStore((state) => state.assetsWithAssigned);
  const assignedData = useAssignedStore((state) => state.assigned);
  const assignedLoading = useAssignedStore((state) => state.loading);
  const shopifyAssetState = useShopifyAsset();
  const { themePages, defaultThemePages } = useQueryThemePages({
    assets: shopifyAssetState.assets,
  });
  const clearCache = useAssignedStore((state) => state.clearCache);
  const setClearCache = useAssignedStore((state) => state.setClearCache);
  const setLoadingByType = useAssignedStore((state) => state.setLoadingByType);
  const setAssetsWithAssigned = useAssignedStore((state) => state.setAssetsWithAssigned);

  const { isEnabledTemplatePageTypeLoading } = useFeatureFlagChecker();

  const shopURL = useMemo(() => shopInfo.shopURL, [shopInfo.shopURL]);
  const isLoadingAsset = useMemo(() => assetsWithAssigned.length === 0, [assetsWithAssigned]);
  const shopifyAssets = useMemo(() => shopifyAssetState.assets, [shopifyAssetState.assets]);
  const assignedKeySet = useMemo(
    () => new Set(assetsWithAssigned?.map((a) => a.key)),
    [assetsWithAssigned],
  );
  const draftAssetTemplates = useMemo<AssetTemplates>(() => {
    if (!unpublishedThemePages.length) return [];

    return unpublishedThemePages.reduce<AssetTemplates>((acc, page) => {
      const draft = createDraftAssetTemplate(page);
      if (!assignedKeySet.has(draft.key)) acc.push(draft);
      return acc;
    }, []);
  }, [unpublishedThemePages, assignedKeySet]);

  useFetchTemplateAssigned({ activePageType: TemplateType.ARTICLE });
  useFetchTemplateAssigned({ activePageType: TemplateType.PAGE });
  useFetchTemplateAssigned({
    activePageType: TemplateType.PRODUCT,
    pagination: isEnabledTemplatePageTypeLoading,
  });
  useFetchTemplateAssigned({ activePageType: TemplateType.COLLECTION });

  const handleMappingTemplateAssigned = useCallback(() => {
    if (!shopifyAssets) return;

    const updatedAssets = buildAssetsWithAssigned({
      shopifyAssets,
      themePages,
      assignedData,
      shopURL,
      defaultThemePages,
    });

    // Skip empty→empty writes: a fresh [] still notifies every subscriber, and this effect
    // re-runs on each render — the pair loops until React bails out with "Maximum update depth".
    if (!updatedAssets.length && !assetsWithAssigned.length) return;
    setAssetsWithAssigned(updatedAssets);
  }, [
    shopifyAssets,
    themePages,
    assignedData,
    shopURL,
    defaultThemePages,
    assetsWithAssigned.length,
    setAssetsWithAssigned,
  ]);

  const handleRefetchAsset = useCallback(async () => {
    // Reset clearCache to false first — ensures Zustand detects the false→true transition below,
    // even if a previous failed API left clearCache stuck at true.
    setClearCache(false);
    setAssetsWithAssigned([]);
    SUPPORT_FETCH_ASSET_TEMPLATE_ASSIGNED.forEach((type) => setLoadingByType(type, true));
    await shopifyAssetState.refetch();
    await refetchThemePage.refetch();
    // Set after awaits so variables in useFetchTemplateAssigned update with the new value.
    setClearCache(true);
  }, [refetchThemePage, setAssetsWithAssigned, setClearCache, setLoadingByType, shopifyAssetState]);

  useEffect(() => {
    // Effect: wait for ALL page types to finish before building assets
    if (isEnabledTemplatePageTypeLoading) return;

    if (shopifyAssetState.isLoading || shopifyAssetState.isRefetching) return;
    if (!refetchThemePage.isReFetchedThemePages) return;
    if (assetsWithAssigned.length) return;
    if (!shopifyAssets || !assignedData) return;
    if (
      !SUPPORT_FETCH_ASSET_TEMPLATE_ASSIGNED.every(
        (type) => assignedData[type] && !assignedLoading[type],
      )
    )
      return;

    handleMappingTemplateAssigned();
  }, [
    clearCache,
    assignedData,
    shopifyAssets,
    assignedLoading,
    assetsWithAssigned.length,
    isEnabledTemplatePageTypeLoading,
    shopifyAssetState.isLoading,
    shopifyAssetState.isRefetching,
    handleMappingTemplateAssigned,
    refetchThemePage.isReFetchedThemePages,
  ]);

  useEffect(() => {
    // Effect: rebuild assets progressively as each page type finishes
    if (!isEnabledTemplatePageTypeLoading) return;

    if (shopifyAssetState.isLoading || shopifyAssetState.isRefetching) return;
    if (!refetchThemePage.isReFetchedThemePages) return;
    if (!shopifyAssets || !assignedData) return;

    // Trigger once at least one page type has finished — process ALL page types so templates
    // for still-loading types appear immediately (disabled) and update when their data arrives.
    const hasAnyPageTypeDone = SUPPORT_FETCH_ASSET_TEMPLATE_ASSIGNED.some(
      (type) => type in assignedData && !assignedLoading[type],
    );
    if (!hasAnyPageTypeDone) return;

    handleMappingTemplateAssigned();
  }, [
    clearCache,
    assignedData,
    shopifyAssets,
    assignedLoading,
    isEnabledTemplatePageTypeLoading,
    shopifyAssetState.isLoading,
    shopifyAssetState.isRefetching,
    refetchThemePage.isReFetchedThemePages,
  ]);

  return {
    defaultThemePages,
    isLoadingAsset,
    assignedLoading,
    assetsWithAssigned,
    draftAssetTemplates,
    isFetchingUnpublishedThemePages,
    handleRefetchAsset,
  };
};
